import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useTenantBrandKit } from '@/lib/tenantBrandKit'
import { workspaceMonogram } from './account'

// The tile is ~32px, so 256px is still 8× its size (sharp at 3× DPR) and the
// pixel scan is 16× cheaper than at 1024px. Memory matters too: iOS caps total
// canvas memory and frees it lazily, so full-size canvases add up.
const TRIM_MAX_PX = 256

// One trim per logo URL for the whole session: the Sidebar and Topbar both
// render the mark, and both remount; neither should repeat the scan.
const trimCache = new Map<string, string | null>()

/**
 * Logos usually arrive with wide transparent margins (a 1024px canvas with
 * the mark in the middle), which shrinks them to a speck in a 30px tile.
 * Crops to the visible pixels; returns null when the image can't be read
 * (a cross-origin host without CORS taints the canvas) so the original shows.
 */
function trimTransparentEdges(img: HTMLImageElement): string | null {
  const canvas = document.createElement('canvas')
  const out = document.createElement('canvas')
  try {
    const scale = Math.min(1, TRIM_MAX_PX / Math.max(img.naturalWidth, img.naturalHeight))
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)
    if (!w || !h) return null
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, w, h)
    const { data } = ctx.getImageData(0, 0, w, h)
    let top = h, left = w, right = -1, bottom = -1
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] > 8) {
          if (x < left) left = x
          if (x > right) right = x
          if (y < top) top = y
          if (y > bottom) bottom = y
        }
      }
    }
    if (right < 0) return null
    const cw = right - left + 1
    const ch = bottom - top + 1
    if (cw > w * 0.92 && ch > h * 0.92) return null // already tight
    out.width = cw
    out.height = ch
    out.getContext('2d')?.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch)
    return out.toDataURL('image/png')
  } catch {
    return null
  } finally {
    // Zero-size canvases hand their backing memory back right away on iOS.
    canvas.width = canvas.height = 0
    out.width = out.height = 0
  }
}

/**
 * The business's mark in the app frame: its Brand kit logo on a white tile
 * (logos are drawn for paper, so white keeps a dark logo readable in dark
 * mode), trimmed to its visible pixels, else its initials on a neutral
 * glass tile (UX-208). It used to be a house glyph, which looked exactly
 * like the Home nav icon right below it.
 */
export function WorkspaceMark({ name, className }: { name: string | null | undefined; className?: string }) {
  const { logoUrl } = useTenantBrandKit()
  const [failed, setFailed] = useState<string | null>(null)
  const [trimmed, setTrimmed] = useState<{ from: string; src: string } | null>(null)
  const mark = workspaceMonogram(name)

  if (logoUrl && failed !== logoUrl) {
    const cached = trimCache.get(logoUrl)
    const src = trimmed?.from === logoUrl ? trimmed.src : cached ?? logoUrl
    return (
      <span
        aria-hidden="true"
        className={cn(
          'shrink-0 inline-flex items-center justify-center overflow-hidden rounded-[10px] p-[3px]',
          'border border-white/[0.10] shadow-[0_2px_8px_-4px_rgba(0,0,0,0.4)]',
          className,
        )}
        style={{ background: '#FFFFFF' }}
      >
        <img
          src={src}
          alt=""
          className="block w-full h-full object-contain"
          onLoad={(e) => {
            if (trimmed?.from === logoUrl) return
            // The other mark may have trimmed this URL while ours loaded.
            const result = trimCache.has(logoUrl) ? trimCache.get(logoUrl)! : trimTransparentEdges(e.currentTarget)
            trimCache.set(logoUrl, result)
            if (result) setTrimmed({ from: logoUrl, src: result })
          }}
          onError={() => setFailed(logoUrl)}
        />
      </span>
    )
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded-[10px] bg-white/[0.06] border border-white/[0.10]',
        'text-[12px] font-semibold tracking-[0.02em] text-white shadow-[inset_0_1px_0_rgb(var(--fg-rgb) / 0.08)]',
        className,
      )}
    >
      {mark || <span className="block h-2 w-2 rounded-full bg-white/70" />}
    </span>
  )
}
